import { Analytics } from '../helpers/analytics';
import { ElementHelpers } from '../helpers/element-helpers';
import { breakpoints } from '../helpers/constants';

export class PersonSlider {
	constructor(elem) {
		this.elem = elem;
		this.sliderContainer = this.elem.querySelector('[data-person-slider-list]');

		this.nextBtn = this.elem.querySelector('[data-person-slider-controls] .next');
		this.prevBtn = this.elem.querySelector('[data-person-slider-controls] .prev');

		this.cards = this.elem.querySelectorAll('[data-slider-item]');

		this.buttonActive = true;

		this.initialized = !!breakpoints.isMedium();

		this.touchXThreshold = 75;
		this.touchTimeThreshold = 50;

		this.offset = 0;

		this.init();
	}

	/**
	 * Set static animate classes
	 * @returns {{slide: string, scale: string}}
	 */
	static get animateClasses() {
		return {
			slide: 'animate-slide',
			scale: 'animate-scale'
		};
	}

	init() {
		if (this.initialized) {
			this.initializeSlider();
		}
		this.addEventHandlers();
	}

	/**
	 * Handles the resize
	 */
	onResize() {
		if (!this.initialized && breakpoints.isMedium()) {
			this.initializeSlider();
		} else if (this.initialized && !breakpoints.isMedium()) {
			this.deinitializeSlider();
		}else if (this.initialized && breakpoints.isMedium()){
			this.handleAnimations('remove');
			this.setInitialTransform();
			this.slideCards(false);
		}
	}

	/**
	 * Initializes the slider
	 */
	initializeSlider() {
		this.initialized = true;

		this.reset();
		this.cloneItems();
		this.setFirstItemActive();
		this.setInitialTransform();

		this.slideCards(false);
	}

	setInitialTransform(){
		this.itemWidth = this.clonedCards[this.currentIndex].getBoundingClientRect().width;
		this.setScreenWidth();
		this.startTransform = (this.screenWidth - this.itemWidth) / 2;
	}

	/**
	 * Resets the slider to initialized state
	 */
	reset() {
		this.offset = 0;
		this.sliderContainer.style.transform = 'translateX(0px)';
		this.setScreenWidth();
		this.buttonActive = true;
		this.resetTouchVariables();
	}

	/**
	 * Deinitializes the slider
	 */
	deinitializeSlider() {
		this.sliderContainer.style.transform = null;
		for (let i = this.clonedCards.length - 1; i >= 0; i--) {
			this.removeClone(i);
			this.clonedCards[i].classList.remove('active');
		}
		this.initialized = false;
	}

	/**
	 * Removes clones after de-initialization
	 * @param slideIndex
	 */
	removeClone(slideIndex) {
		if (this.clonedCards[slideIndex].classList.contains('clone')) {
			ElementHelpers.removeElement(this.clonedCards[slideIndex]);
		}
	}

	/**
	 * Set screen width
	 */
	setScreenWidth() {
		this.screenWidth = document.body.clientWidth;
	}

	/**
	 * Clones the first and last 2 items items, and put it in front and back of the slider, to create infinite loop
	 */
	cloneItems() {
		for (let i = this.cards.length - 1; i > this.cards.length - 3; i--) {
			const clone = this.cards[i].cloneNode(true);
			clone.classList.add('clone');
			this.sliderContainer.prepend(clone);
		}

		for (let i = 0; i < 2; i++) {
			const clone = this.cards[i].cloneNode(true);
			clone.classList.add('clone');
			this.sliderContainer.append(clone);
		}

		this.clonedCards = this.elem.querySelectorAll('[data-slider-item]');
		this.currentIndex = this.getOriginalIndex();
	}

	/**
	 * Sets the first index on Active
	 */
	setFirstItemActive() {
		this.clonedCards[this.currentIndex].classList.add('active');
	}

	/**
	 * Slide cards to the next or previous card
	 * @param hasTransition
	 */
	slideCards(hasTransition = true) {
		if (hasTransition && !this.sliderContainer.classList.contains(PersonSlider.animateClasses.slide)) {
			this.handleAnimations('add');
		}

		const move = this.currentIndex * this.itemWidth;
		this.offset = this.startTransform - move;
		this.sliderContainer.style.transform = 'translateX(' + this.offset + 'px)';
	}

	/**
	 * Add all necessary eventHandlers
	 */
	addEventHandlers() {
		window.addEventListener('resize', () => {
			this.onResize();
		});

		this.nextBtn.addEventListener('click', () => {
			if (this.initialized) {
				this.toggleActiveCard('next');
			}
		});

		this.prevBtn.addEventListener('click', () => {
			if (this.initialized) {
				this.toggleActiveCard('previous');
			}
		});

		this.sliderContainer.addEventListener('touchstart', (event) => {
			if (this.initialized && this.buttonActive) {
				this.touchTime0 = Date.now();
				this.touchStartX = event.touches[0].clientX;
				this.sliderContainer.classList.remove(PersonSlider.animateClasses.slide);
			}
		});

		this.sliderContainer.addEventListener('touchmove', (event) => {
			event.preventDefault();
			if (this.initialized && this.buttonActive) {
				this.touchEndX = event.touches[0].clientX;
				this.xDiff = this.touchStartX - this.touchEndX;
				this.xDiff = this.xDiff.toFixed(2);
				this.sliderContainer.style.transform = 'translateX(' + (this.offset - this.xDiff) + 'px)';
			}
		}, {passive: false});

		this.sliderContainer.addEventListener('touchend', () => {
			if (this.initialized && this.buttonActive) {
				this.handleAnimations('add');
				this.touchTime1 = Date.now();
				const timeDiff = this.touchTime1 - this.touchTime0;

				this.offset = (this.offset - this.xDiff).toFixed(2);

				if (timeDiff > this.touchTimeThreshold && Math.abs(this.xDiff) > this.touchXThreshold) {
					this.xDiff > 0 ? this.toggleActiveCard('next') : this.toggleActiveCard('previous');
				} else if (this.xDiff < this.touchXThreshold) {
					this.slideCards(false);
				}
				this.resetTouchVariables();
			}
		});
	}

	/**
	 * Reset the Touch variables for swiping
	 */
	resetTouchVariables() {
		this.touchStartX = null;
		this.touchEndX = null;
		this.touchTime0 = null;
		this.touchTime1 = null;
		this.xDiff = null;
	}

	/**
	 * Go to next or previous card
	 * @param direction
	 */
	toggleActiveCard(direction) {
		if(!this.buttonActive) return;
		this.clonedCards[this.currentIndex].classList.remove('active');

		if (direction === 'next') {
			this.currentIndex++;
		} else {
			this.currentIndex--;
		}

		this.slideCards();

		this.clonedCards[this.currentIndex].classList.add('active');

		if (this.clonedCards[this.currentIndex].classList.contains('clone')) {
			this.relocateOnLast(direction);
		}

		const analyticsCategory = this.elem.getAttribute(Analytics.analyticsTags.component) || '';
		const data = {
			action: 'click - interact',
			category: analyticsCategory,
			label: direction
		};
		Analytics.measure(data);
	}

	/**
	 * Go to the first or last index, if reaches the last card to create infinite loop
	 */
	relocateOnLast(direction) {
		this.buttonActive = false;
		setTimeout(() => {
			this.handleAnimations('remove');
			this.clonedCards[this.currentIndex].classList.remove('active');
			if (direction === 'next') {
				this.currentIndex = this.getOriginalIndex();
			} else {
				this.currentIndex = this.cards.length + 1;
			}
			this.slideCards(false);
			this.clonedCards[this.currentIndex].classList.add('active');
		}, 250);
		setTimeout(() => {
			this.handleAnimations('add');
			this.buttonActive = true;
		}, 500);
	}

	/**
	 * Get first index of non-cloned card
	 */
	getOriginalIndex() {
		for (let i = 0; i < this.clonedCards.length; i++) {
			if (!this.clonedCards[i].classList.contains('clone')) {
				return i;
			}
		}
	}

	/**
	 * Handle animations
	 * @param modification
	 */
	handleAnimations(modification){
		if(modification === 'add'){
			this.sliderContainer.classList.add(PersonSlider.animateClasses.slide);
			this.sliderContainer.classList.add(PersonSlider.animateClasses.scale);
		}else{
			this.sliderContainer.classList.remove(PersonSlider.animateClasses.slide);
			this.sliderContainer.classList.remove(PersonSlider.animateClasses.scale);
		}
	}

	static getSelector() {
		return '[data-person-slider]';
	}
}

